import {
  Observable,
  Subject,
  UnaryFunction,
  bufferWhen,
  pipe,
  tap,
} from 'rxjs';

const FLUSH_TIMEOUT = 64;
const MAX_DURATION = 1000;
const FLUSH = 'flush';

type Options = {
  delay?: number;
  maxDelay?: number;
};

/**
 * Begins buffering stream iterations upon the first iteration. It then waits
 * for a specified `delay` time for the next iteration to arrive.
 * If the next iteration does not arrive within the `delay` time, the buffer is
 * cleared.
 * The total time for allocation is capped by the `maxDelay` time.
 * @param  options Configurable parameters to modify the allocation behavior.
 * @returns Returns an operator function that manages the buffering of stream iterations.
 */
export const allocateToBuffer = <T>({
  delay = FLUSH_TIMEOUT,
  maxDelay = MAX_DURATION,
}: Options = {}): UnaryFunction<Observable<T>, Observable<T[]>> => {
  const flush$ = new Subject<typeof FLUSH>();
  let timeout = 0;
  let expiresAt = 0;
  return pipe(
    tap(() => {
      window.clearTimeout(timeout);
      if (expiresAt !== 0 && performance.now() > expiresAt) {
        expiresAt = 0;
        timeout = 0;
        flush$.next(FLUSH);
        return;
      }
      timeout = window.setTimeout(() => {
        expiresAt = 0;
        flush$.next(FLUSH);
      }, delay);
      expiresAt = expiresAt === 0 ? performance.now() + maxDelay : expiresAt;
    }),
    bufferWhen(() => flush$)
  );
};
